"use client";

import { c as _c } from "react/compiler-runtime";
import { type ChangeEvent, useCallback, useId, useState } from "react";
import { z } from "zod";
import { Input } from "@defuse/ui/input";
import { Label } from "@defuse/ui/label";
import { numToStr } from "@/lib/type-safe-stringify";
interface UsdPriceRangeFilterProps {
  minUsdPrice: number | undefined;
  maxUsdPrice: number | undefined;
  updateFilters: (filters: {
    minUsdPrice: number | undefined;
    maxUsdPrice: number | undefined;
  }) => void;
  setError: (error: string) => void;
}
const priceFilterSchema = z.object({
  minUsdPrice: z.coerce.number().min(0).optional(),
  maxUsdPrice: z.coerce.number().min(0).optional()
}).refine(data => {
  const min = data.minUsdPrice ?? 0;
  const max = data.maxUsdPrice ?? 0;
  return !(min > 0 && max > 0 && min > max);
}, {
  message: "Max USD Price should be greater than or equal to Min USD Price",
  path: ["maxUsdPrice"]
});
export function UsdPriceRangeFilter(t0) {
  const $ = _c(33);
  const {
    minUsdPrice,
    maxUsdPrice,
    updateFilters,
    setError
  } = t0;
  const minId = useId();
  const maxId = useId();
  let t1;
  if ($[0] !== minUsdPrice) {
    t1 = () => minUsdPrice !== undefined ? numToStr(minUsdPrice) : "";
    $[0] = minUsdPrice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [minInputValue, setMinInputValue] = useState(t1);
  let t2;
  if ($[2] !== maxUsdPrice) {
    t2 = () => maxUsdPrice !== undefined ? numToStr(maxUsdPrice) : "";
    $[2] = maxUsdPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [maxInputValue, setMaxInputValue] = useState(t2);
  let t3;
  if ($[4] !== maxInputValue || $[5] !== setError || $[6] !== updateFilters) {
    t3 = t4 => {
      const {
        target: t5
      } = t4;
      const {
        value
      } = t5;
      setMinInputValue(value);
      validateAndSetPriceRangeFilter({
        minInputValue: value,
        maxInputValue,
        updateFilters,
        setError
      });
    };
    $[4] = maxInputValue;
    $[5] = setError;
    $[6] = updateFilters;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleMinValueChange = t3;
  let t4;
  if ($[8] !== minInputValue || $[9] !== setError || $[10] !== updateFilters) {
    t4 = t5 => {
      const {
        target: t6
      } = t5;
      const {
        value: value_0
      } = t6;
      setMaxInputValue(value_0);
      validateAndSetPriceRangeFilter({
        minInputValue,
        maxInputValue: value_0,
        updateFilters,
        setError
      });
    };
    $[8] = minInputValue;
    $[9] = setError;
    $[10] = updateFilters;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleMaxValueChange = t4;
  let t5;
  if ($[12] !== minId) {
    t5 = <Label htmlFor={minId} className="text-muted-foreground text-xs">Min USD Price</Label>;
    $[12] = minId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleMinValueChange || $[15] !== minId || $[16] !== minInputValue) {
    t6 = <Input id={minId} type="text" inputMode="decimal" value={minInputValue} onChange={handleMinValueChange} placeholder="0.00" className="w-full" />;
    $[14] = handleMinValueChange;
    $[15] = minId;
    $[16] = minInputValue;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <div className="flex flex-1 flex-col gap-1">{t5}{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== maxId) {
    t8 = <Label htmlFor={maxId} className="text-muted-foreground text-xs">Max USD Price</Label>;
    $[21] = maxId;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== handleMaxValueChange || $[24] !== maxId || $[25] !== maxInputValue) {
    t9 = <Input id={maxId} type="text" inputMode="decimal" value={maxInputValue} onChange={handleMaxValueChange} placeholder="0.00" className="w-full" />;
    $[23] = handleMaxValueChange;
    $[24] = maxId;
    $[25] = maxInputValue;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <div className="flex flex-1 flex-col gap-1">{t8}{t9}</div>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t7) {
    t11 = <div className="flex flex-col gap-1"><div className="flex items-end gap-2">{t7}{t10}</div></div>;
    $[30] = t10;
    $[31] = t7;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function validateAndSetPriceRangeFilter({
  minInputValue,
  maxInputValue,
  updateFilters,
  setError
}: {
  minInputValue: string;
  maxInputValue: string;
} & Pick<UsdPriceRangeFilterProps, "updateFilters" | "setError">) {
  const result = priceFilterSchema.safeParse({
    minUsdPrice: minInputValue === "" ? undefined : minInputValue,
    maxUsdPrice: maxInputValue === "" ? undefined : maxInputValue
  });
  if (result.success) {
    setError("");
    updateFilters({
      minUsdPrice: result.data.minUsdPrice,
      maxUsdPrice: result.data.maxUsdPrice
    });
  } else {
    setError(z.prettifyError(result.error));
  }
}